import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, Alert, Snackbar, CircularProgress,
  Chip, Stack, Tooltip
} from '@mui/material';
import { roleService } from '../../services/roleService';
import { EmptyState, PageHeader, SectionCard } from '../ui';

// Mô tả cho từng role
const ROLE_DESCRIPTIONS: Record<string, string> = {
  'auto_role': 'Quyền sử dụng Automation (auto change)',
  '3meets_role': 'Quyền sử dụng 3 meet config (3m-config)',
  '3meets_gemini_role': 'Quyền sử dụng 3meets gemini config (3m-config-deal)',
  'keybox_rotate_role': 'Quyền update new keybox (3m-config-keyrotate)',
};

// Màu chip cho từng role
const ROLE_COLORS: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
  'auto_role': 'info',
  '3meets_role': 'success',
  '3meets_gemini_role': 'warning',
  'keybox_rotate_role': 'error',
};

export const RoleManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null); // "userType_role" đang saving
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [userTypes, setUserTypes] = useState<string[]>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  // Map: "userType_role" -> boolean
  const [roleMatrix, setRoleMatrix] = useState<Record<string, boolean>>({});

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await roleService.getUserTypeRoles();

      setAvailableRoles(data.availableRoles);
      setUserTypes(data.userTypes);

      // Build role matrix
      const matrix: Record<string, boolean> = {};
      for (const r of data.roles) {
        if (r.is_enabled) {
          matrix[`${r.user_type}_${r.role}`] = true;
        }
      }
      setRoleMatrix(matrix);
    } catch (err: any) {
      setError('Failed to load role data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const getKey = (userType: string, role: string) => `${userType}_${role}`;

  const isChecked = (userType: string, role: string) => {
    return !!roleMatrix[getKey(userType, role)];
  };

  const handleToggle = async (userType: string, role: string) => {
    const key = getKey(userType, role);
    const currentlyChecked = !!roleMatrix[key];

    // Optimistic update
    setRoleMatrix(prev => ({
      ...prev,
      [key]: !currentlyChecked
    }));
    setSaving(key);

    try {
      if (currentlyChecked) {
        // Bỏ role: xoá khỏi DB
        const result = await roleService.removeRole(userType, role);
        setSuccessMessage(result.message);
      } else {
        // Gán role: thêm vào DB
        const result = await roleService.assignRole(userType, role, true);
        setSuccessMessage(result.message);
      }
    } catch (err: any) {
      // Rollback on error
      setRoleMatrix(prev => ({
        ...prev,
        [key]: currentlyChecked
      }));
      setError('Failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Config"
        title="Role management"
        description="Gán role cho từng User Type. Nếu userType có role tương ứng, client sẽ được cấp quyền xử lý tính năng đó."
      >
        {/* The legend doubles as the key for the checkbox colours in the matrix. */}
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {availableRoles.map(role => (
            <Tooltip key={role} title={ROLE_DESCRIPTIONS[role] || role}>
              <Chip
                label={role}
                color={ROLE_COLORS[role] || 'default'}
                variant="outlined"
                size="small"
              />
            </Tooltip>
          ))}
        </Stack>
      </PageHeader>

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 140 }}>User type</TableCell>
              {availableRoles.map(role => (
                <TableCell key={role} align="center">
                  <Tooltip title={ROLE_DESCRIPTIONS[role] || role}>
                    <span>{role.replace('_role', '')}</span>
                  </Tooltip>
                </TableCell>
              ))}
              <TableCell align="center">Active roles</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userTypes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={availableRoles.length + 2} sx={{ p: 0 }}>
                  <EmptyState
                    title="No user types found"
                    description="User types are derived from existing licences - issue one to populate this matrix."
                  />
                </TableCell>
              </TableRow>
            ) : (
              userTypes.map((userType) => {
                const activeRoles = availableRoles.filter(r => isChecked(userType, r));
                return (
                  <TableRow key={userType} hover>
                    <TableCell>
                      <Chip
                        label={userType}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.primary' }}
                      />
                    </TableCell>
                    {availableRoles.map(role => {
                      const key = getKey(userType, role);
                      const checked = isChecked(userType, role);
                      const isSaving = saving === key;

                      return (
                        <TableCell key={role} align="center" sx={{ py: 0.5 }}>
                          {isSaving ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Checkbox
                              checked={checked}
                              onChange={() => handleToggle(userType, role)}
                              color={ROLE_COLORS[role] || 'primary'}
                              inputProps={{ 'aria-label': role + ' for ' + userType }}
                            />
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center">
                      {activeRoles.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                          {activeRoles.map(r => (
                            <Chip
                              key={r}
                              label={r.replace('_role', '')}
                              size="small"
                              color={ROLE_COLORS[r] || 'default'}
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No roles
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Role Config Info */}
      <Box sx={{ mt: 2.5 }}>
      <SectionCard title="Role → config mapping">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label="keyrotate" size="small" color="error" sx={{ width: 80 }} />
            <Typography variant="body2" color="text.secondary">
              → phoneVersion: <code>3m-config-keyrotate</code>, pifVersion: <code>3m-keyrotateda9dbac</code>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label="deal" size="small" color="warning" sx={{ width: 80 }} />
            <Typography variant="body2" color="text.secondary">
              → phoneVersion: <code>3m-config-deal</code>, pifVersion: <code>3m-deal7faa0990dbac</code>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label="3meets" size="small" color="success" sx={{ width: 80 }} />
            <Typography variant="body2" color="text.secondary">
              → phoneVersion: <code>3m-config</code>, pifVersion: <code>3m-acc97fa0990dabac</code>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label="auto" size="small" color="info" sx={{ width: 80 }} />
            <Typography variant="body2" color="text.secondary">
              → Cho phép hiển thị Automation tab ở client C#
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label="default" size="small" sx={{ width: 80, bgcolor: 'background.subtle' }} />
            <Typography variant="body2" color="text.secondary">
              → phoneVersion: <code>2m-config</code>, pifVersion: <code>2m-82247cf3d1874768</code> (khi không có deal/3meets role)
            </Typography>
          </Box>
        </Box>
      </SectionCard>
      </Box>

      {/* Snackbar messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)} variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={() => setError(null)} variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleManagement;
